import { Injectable } from '@angular/core';
import { catchError, filter, map, Observable, of, tap } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';
import { PagedMetadata } from '../../shared/models/page-metadata.model';
import { ICreateUser, IPatchUser, IEntraIdUser, IUser, Roles } from '@cod-monorepo/interfaces';
import { CacheService } from '../../shared/services/cache.service';

@Injectable({
    providedIn: 'root'
})
export class UserManagementService {
    private apiUrl = `${environment.apiUrl}/users`;
    private cache: CacheService<IUser>;

    constructor(private httpClient: HttpClient) {
        this.cache = new CacheService<IUser>();
    }

    public getUsers(data: {
        page: number;
        pageSize: number;
        searchTerm?: string;
        role?: Roles;
    }): Observable<{ items: IUser[]; metadata: PagedMetadata }> {
        const params = {
            page: data.page,
            pageSize: data.pageSize
        } as {
            [key: string]: string | number;
        };

        if (data.searchTerm?.length) {
            params['searchTerm'] = data.searchTerm;
        }

        let endpoint = this.apiUrl;
        switch (data.role) {
            case Roles.CSR:
                endpoint = `${this.apiUrl}/csr`;
                break;
            case Roles.MANAGER:
                endpoint = `${this.apiUrl}/manager`;
                break;
            case Roles.INVESTIGATOR:
                endpoint = `${this.apiUrl}/investigator`;
                break;
            case Roles.FINANCE_MANAGER:
                endpoint = `${this.apiUrl}/finance-manager`;
                break;
            case Roles.CREDIT_REVIEWER:
                endpoint = `${this.apiUrl}/credit-reviewer`;
                break;
            default:
                endpoint = this.apiUrl;
                break;
        }

        return this.httpClient.get<{ items: IUser[]; metadata: PagedMetadata }>(`${endpoint}`, {
            params
        });
    }

    public getUsersWithRole(role: Roles): Observable<IUser[]> {
        return this.httpClient.get<IUser[]>(`${this.apiUrl}/role/${role}`);
    }

    public createUser(user: ICreateUser): Observable<IUser> {
        return this.httpClient.post<IUser>(`${this.apiUrl}`, user);
    }

    public updateUser(gid: string, user: IPatchUser): Observable<IUser> {
        return this.httpClient.patch<IUser>(`${this.apiUrl}/${gid}`, user).pipe(
            tap(user => {
                this.cache.set(gid, user);
            })
        );
    }

    public deleteUser(gid: string): Observable<IUser> {
        return this.httpClient.delete<IUser>(`${this.apiUrl}/${gid}`);
    }

    public getUser(gid: string): Observable<IUser> {
        return this.cache.get(gid, this.httpClient.get<IUser>(`${this.apiUrl}/${gid}`).pipe(filter(user => !!user)));
    }

    public getMe(): Observable<IUser> {
        return this.httpClient.get<IUser>(`${environment.apiUrl}/me`);
    }

    public getUsersFromEntraId(params: {
        gid?: string;
        firstName?: string;
        surName?: string;
    }): Observable<IEntraIdUser[]> {
        if (!params.gid) delete params.gid;
        if (!params.firstName) delete params.firstName;
        if (!params.surName) delete params.surName;

        return this.httpClient.get<IEntraIdUser[]>(`${this.apiUrl}/entra-id`, { params });
    }

    public getProfilePicture(): Observable<string | undefined> {
        return this.httpClient
            .get<{ data: string; encoding: string; contentType: string }>(`${environment.apiUrl}/v1/me/profile-picture`)
            .pipe(
                map(response => `data:${response.contentType};${response.encoding},${response.data}`),
                catchError(() => of(undefined))
            );
    }
}
